<template>
	<div
		class="profile-pic-avatar"
		:title="user.name || user.fullname"
		:style="cssVars"
	>
		<img
			v-if="hasProfilePic && !loadError && !customtext"
			:src="imageUrl"
			:alt="$t('settings_profile_sheet_title')"
			class="ph-no-capture"
			@error="onLoadError"
		/>
		<div
			class="profile-pic-initials"
			v-if="(!hasProfilePic || loadError) && !customtext"
			v-text="initials"
		/>
		<div
			class="profile-pic-initials custom-text"
			v-if="customtext"
			v-text="customtext"
		/>
		<div
			v-if="enableOnline"
			class="online-indicator"
			:class="{ active: user.isonline }"
		></div>
		<img
			v-if="edit_icon"
			class="edit-icon"
			src="/images/pronto/edit.svg"
			alt="Edit"
		/>
	</div>
</template>

<script>
import store, { getUser } from '@/store'
import { getInitials } from '@/helpers/utils'

export default {
	name: 'ProfilePic',
	props: {
		user: {
			type: Object,
			default: () => ({}),
		},
		size: {
			type: String,
			default: '32px',
		},
		customtext: {
			type: String,
			default: null,
			required: false,
		},
		enableOnline: {
			type: Boolean,
			default: null,
		},
		onlineSize: {
			type: String,
			default: '15px',
		},
		edit_icon: {
			type: Boolean,
			default: null,
		},
	},

	data() {
		return { loadError: false }
	},

	mounted() {
		this.$onEventBus('user-update', (userId) => {
			// This always keeps the user.isonline field up to date
			if (!this.user || this.user.id !== userId) return
			const user = getUser(userId)
			if (!user) return
			// eslint-disable-next-line vue/no-mutating-props
			this.user.isonline = user.isonline
		})
	},

	computed: {
		cssVars() {
			return { '--size': this.size, '--online-size': this.onlineSize }
		},
		initials() {
			if (this.user.name) {
				return this.user.name[0].toUpperCase()
			}
			return getInitials(this.user)
		},
		imageUrl() {
			if (this.user.profilepicpath) {
				return store.userProfile(this.user)
			}
			return this.user.profilepicurl
		},

		hasProfilePic() {
			if (this.user.hasOwnProperty('profilepic')) {
				return this.user.profilepic
			} else {
				return this.user.profilepicurl
			}
		},
	},

	methods: {
		onLoadError(e) {
			console.error('[ProfilePic] load error', e)
			this.loadError = true
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
$online-offset: -3px;

.profile-pic-avatar {
	position: relative;
	width: var(--size);
	height: var(--size);
	border-radius: calc(var(--size) / 2);
	border: 1px solid var(--neutral-200);
	img {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		width: 100%;
		height: 100%;
		border-radius: calc(var(--size) / 2);
		object-fit: cover;
		background-color: var(--neutral-500);
	}
	.profile-pic-initials {
		width: 100%;
		height: 100%;
		font-size: calc(var(--size) / 2.25);
		letter-spacing: 0.05em;
		color: white;
		background: var(--neutral-500);
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: calc(var(--size) / 2);
		text-transform: uppercase;
		@include fontFamily(500);
	}
	.online-indicator {
		position: absolute;
		width: var(--online-size);
		height: var(--online-size);
		border: 3px solid white;
		border-width: calc(var(--online-size) / 5);
		border-radius: calc(var(--online-size) / 2);
		background: var(--teal-300);
		overflow: hidden;
		bottom: $online-offset;
		right: $online-offset;
		opacity: 0;
		transition: opacity 0.3s linear;
		&.active {
			opacity: 1;
		}
	}
	.edit-icon {
		width: 23px;
		height: 23px;
		position: absolute;
		bottom: -2px;
		right: -8px;
		top: auto;
		left: auto;
	}
}
</style>
